import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ToastHelper from 'general/helpers/ToastHelper';
import AppResource from 'general/constants/AppResource';
import UIHelper from 'general/helpers/UIHelper';
import KTRadio, { KTRadioType } from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import Utils from 'general/utils/Utils';
import AppColors from 'general/constants/AppColors';

DialogReleaseVoucherView.propTypes = {
  items: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
};

function DialogReleaseVoucherView({ items, onCancel, onDone }) {
  // MARK: ---- PROPERTIES ----
  const [identityType, setIdentityType] = useState(AgencyConstants.VoucherIdentityType.ANY);
  const [selectDriver, setSelectDriver] = useState(null);
  const [selectVehicle, setSelectVehicle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [readyToValidate, setReadyToValidate] = useState(false);

  useEffect(() => {
    if (!Array.isArray(items)) {
      setIdentityType(items?.identityType);
    } else {
      setIdentityType(AgencyConstants.VoucherIdentityType.ANY);
    }
  }, [items]);

  const dropdownDriverEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="driverId"
        name="driverId"
        ignoreConfirmClick={true}
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
              ${readyToValidate && !Utils.isValid(selectDriver?.id) ? 'button-invalid' : ''}`}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            type="button"
          >
            {selectDriver?.name ?? 'Chọn khách hàng'}
          </button>
        }
        selectedValue={selectDriver}
        subTitleType="email"
        dropdownMenuItems={[{ name: 'Khách hàng', role: 'DRIVER' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.Driver.requestGetDriverList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.drivers?.map((row) => ({
                id: row?.userId,
                name: row?.fullname,
                item: row,
                email: row?.email,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectDriver(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchPlaceholder="Tìm kiếm..."
        searchFormClassName="mx-3 my-1 search-form-border"
        defaultValue={{
          name: 'Khách hàng',
          role: 'DRIVER',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectDriver(null);
            })}
          </div>
        }
      />
    );
  }, [selectDriver, readyToValidate]);

  const dropdownVehicleEl = useMemo(() => {
    return (
      <CustomDropdown2
        id="vehicleId"
        name="vehicleId"
        ignoreConfirmClick={true}
        toggleElement={
          <button
            className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between
              ${readyToValidate && !Utils.isValid(selectVehicle?.id) ? 'button-invalid' : ''}`}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            type="button"
          >
            {selectVehicle?.name ?? 'Chọn xe'}
          </button>
        }
        selectedValue={selectVehicle}
        subTitleType="subTitle"
        dropdownMenuItems={[{ name: 'Mã định danh', role: 'VEHICLE_IDENTITY' }]}
        getSubItems={async (item) => {
          return ChargeHelper.API.Identifier.requestGetIdentifierList({
            page: item?.page || 0,
            limit: 10,
            q: item?.q || '',
          }).then((data) => {
            return {
              values: data.vehicleIdentities?.map((row) => ({
                id: row?.vehicleIdentityId,
                name: row?.code,
                subTitle: row?.vehicle?.plate?.trim() ? row.vehicle.plate : 'Chưa biển số',
                item: row,
              })),
              total: data.total,
            };
          });
        }}
        dropdownMenuStyle={{ maxHeight: '30vh' }}
        dropdownMenuClassName="overflow-auto w-100"
        onChange={(value) => {
          setSelectVehicle(value);
        }}
        isDropdownForm={true}
        titleSearchable={true}
        searchable={true}
        searchPlaceholder="Tìm kiếm mã MAC/EVCC, biển số,..."
        searchFormClassName="mx-3 my-1 search-form-border"
        defaultValue={{
          name: 'Mã định danh',
          role: 'VEHICLE_IDENTITY',
        }}
        footerEl={
          <div
            className="p-4 position-sticky sticky-bottom bg-white"
            style={{
              borderTop: `1px solid ${AppColors.gray[300]}`,
            }}
          >
            {UIHelper.buildNegativeButton('Bỏ chọn', () => {
              setSelectVehicle(null);
            })}
          </div>
        }
      />
    );
  }, [selectVehicle, readyToValidate]);

  // MARK: ---- METHODS ----

  const handleSubmit = async () => {
    setReadyToValidate(true);
    if (identityType === AgencyConstants.VoucherIdentityType.DRIVER && !selectDriver) {
      ToastHelper.showError('Vui lòng chọn khách hàng');
      return;
    }
    if (identityType === AgencyConstants.VoucherIdentityType.VEHICLE && !selectVehicle) {
      ToastHelper.showError('Vui lòng chọn xe');
      return;
    }

    setLoading(true);

    if (!Array.isArray(items)) {
      const param = {
        driverId: identityType === AgencyConstants.VoucherIdentityType.DRIVER ? selectDriver?.id : null,
        vehicleIdentityId:
          identityType === AgencyConstants.VoucherIdentityType.VEHICLE ? selectVehicle?.id : null,
      };

      const res = await AgencyHelper.API.requestUpdateVoucher(items.voucherId, param);

      if (res && _.isBoolean(res)) {
        ToastHelper.showSuccess('Phát hành voucher thành công');
        onDone?.();
      } else {
        ToastHelper.showError(res);
      }
    } else {
      const param = {
        voucherIds: items,
      };

      const res = await AgencyHelper.API.requestReleaseListVoucher(param);

      if (res && _.isBoolean(res)) {
        ToastHelper.showSuccess(`Phát hành thành công ${items?.length} voucher`);
        onDone?.();
      } else {
        ToastHelper.showError(res);
      }
    }

    setLoading(false);
  };

  return (
    <div>
      <div className="d-flex flex-column align-items-center justify-content-center text-center px-5 pt-6 gap-2">
        <div className="mb-4">
          <img src={AppResource.images.dialogIcons.dialogReleaseVoucher} alt="Release Voucher" />
        </div>

        <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
          Phát hành voucher
        </span>
        <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
          {!Array.isArray(items) ? (
            'Bạn có chắc muốn phát hành voucher đã chọn không?'
          ) : (
            <>
              Bạn có chắc muốn phát hành{' '}
              <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[600])}
              >[{String(items?.length || 0).padStart(2, '0')}] voucher</span>{' '}
              đã chọn không?
            </>
          )}
        </span>
      </div>

      <div className="px-5 my-4">
        <div className="mb-2" style={UIHelper.buildFontStyle('700', '13px', AppColors.gray[800])}>
          Đối tượng nhận voucher
        </div>
        <div className="radio-inline w-100 mb-3">
          <div className="d-flex align-items-center gap-5">
            <KTRadio
              name="identityTypeDriver"
              label="Khách hàng"
              type={KTRadioType.outline}
              additionalClassName="radio-success"
              checked={identityType === AgencyConstants.VoucherIdentityType.DRIVER}
              onChange={() => setIdentityType(AgencyConstants.VoucherIdentityType.DRIVER)}
              disabled={true}
            />
            <KTRadio
              name="identityTypeVehicle"
              label="Xe"
              type={KTRadioType.outline}
              additionalClassName="radio-success"
              checked={identityType === AgencyConstants.VoucherIdentityType.VEHICLE}
              onChange={() => setIdentityType(AgencyConstants.VoucherIdentityType.VEHICLE)}
              disabled={true}
            />
            <KTRadio
              name="identityTypeAny"
              label="Chưa xác định"
              type={KTRadioType.outline}
              additionalClassName="radio-success"
              checked={identityType === AgencyConstants.VoucherIdentityType.ANY}
              onChange={() => setIdentityType(AgencyConstants.VoucherIdentityType.ANY)}
              disabled={true}
            />
          </div>
        </div>
      </div>

      <div className="row m-0 p-0 px-3">
        <div className="col-12 mb-4 px-2">
          {identityType === AgencyConstants.VoucherIdentityType.DRIVER && dropdownDriverEl}
          {identityType === AgencyConstants.VoucherIdentityType.VEHICLE && dropdownVehicleEl}
        </div>
      </div>

      <div
        className="d-flex align-items-center gap-3 px-4 py-3"
        style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
      >
        <div className="flex-fill">
          {UIHelper.buildNegativeButton('Hủy bỏ', onCancel)}
        </div>

        <div className="flex-fill">
          {UIHelper.buildGreenButton('Phát hành', handleSubmit, null, false, loading)}
        </div>
      </div>
    </div>
  );
}

export default DialogReleaseVoucherView;